import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Card, Col, Empty, Row, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { FaBuildingColumns, FaCircleInfo, FaMobile, FaWallet, FaRightLeft } from 'react-icons/fa6';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Transaction } from '../../types/transaction.types';
import { PaymentAccount } from '../../types/payment-account.types';
import { RetailerSummaryReport } from '../../types/reports.types';

const { Title, Text } = Typography;

interface TransactionsResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Transaction[];
}

type BadgeStatusType = 'default' | 'processing' | 'success' | 'error' | 'warning';

const Withdrawals = () => {
	// Fetch withdrawal transactions
	const { data, isLoading, error } = useQuery<TransactionsResponse>({
		queryKey: ['withdrawalTransactions'],
		queryFn: async () => {
			const { data } = await axios.get('/payments/retailers/withdrawal-transactions', {
				params: {
					page: 1,
					limit: 20,
				},
				withCredentials: true,
			});
			return data;
		},
	});

	// Fetch retailer summary data for balance information
	const { data: summaryData, isLoading: isSummaryLoading } = useQuery<{ results: RetailerSummaryReport }>({
		queryKey: ['retailerSummary'],
		queryFn: async () => {
			const { data } = await axios.get('/reports/retailers/summary', { withCredentials: true });
			return data;
		},
	});

	// Format currency for display
	const formatCurrency = (amount: string | number) => {
		const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'UGX',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(numAmount);
	};

	// Define table columns
	const columns = [
		{
			title: 'Date',
			dataIndex: 'date',
			key: 'date',
			render: (date: string) => (
				<div className="whitespace-nowrap">
					<div className="font-medium">{dayjs(date).format('MMM D, YYYY')}</div>
					<div className="text-xs text-gray-500">{dayjs(date).format('h:mm A')}</div>
				</div>
			),
		},
		{
			title: 'Transaction ID',
			dataIndex: 'id',
			key: 'id',
			render: (id: number) => <span className="font-mono font-medium">#{id}</span>,
		},
		{
			title: 'Account',
			dataIndex: 'payment_account',
			key: 'payment_account',
			render: (account: PaymentAccount | null) => {
				if (!account) return <Text type="secondary">N/A</Text>;

				return (
					<div className="flex items-center gap-2">
						{account.type === 'mobile_money' ? <FaMobile className="text-purple-600" /> : <FaBuildingColumns className="text-blue-600" />}
						<div>
							<div className="font-medium">{account.account_number}</div>
							{account.type === 'bank' && (
								<div className="text-xs text-gray-500">
									{account.bank_name} • {account.branch_name}
								</div>
							)}
						</div>
					</div>
				);
			},
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: string) => <span className="font-medium">{formatCurrency(amount)}</span>,
		},
		{
			title: 'Gateway',
			dataIndex: 'gateway',
			key: 'gateway',
			render: (gateway: string, record: Transaction) => (
				<Space>
					<Tag color={gateway === 'beyonic' ? 'purple' : gateway === 'bank' ? 'blue' : 'default'}>
						{gateway === 'beyonic' ? 'Mobile Money' : gateway === 'bank' ? 'Bank Transfer' : gateway}
					</Tag>
					{record.gateway_trx_id && (
						<Tooltip title={`Gateway Ref: ${record.gateway_trx_id}`}>
							<span className="cursor-help text-gray-400">
								<FaCircleInfo size={14} />
							</span>
						</Tooltip>
					)}
				</Space>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string, record: Transaction) => {
				let color: BadgeStatusType = 'default';
				let statusText = '';

				switch (status) {
					case 'pending':
						color = 'processing';
						statusText = 'Pending';
						break;
					case 'success':
						color = 'success';
						statusText = 'Success';
						break;
					case 'failed':
						color = 'error';
						statusText = 'Failed';
						break;
					default:
						color = 'default';
						statusText = status;
				}

				return (
					<div>
						<Badge
							status={color}
							text={statusText}
							className="whitespace-nowrap"
						/>
						{record.error_message && <div className="mt-1 text-xs text-red-500">{record.error_message}</div>}
					</div>
				);
			},
		},
	];

	// Render loading state
	if (isLoading) {
		return (
			<AppLayout
				title="Withdrawal Transactions"
				subtitle="View all withdrawal transactions"
			>
				<Card>
					<Skeleton
						active
						paragraph={{ rows: 10 }}
					/>
				</Card>
			</AppLayout>
		);
	}

	// Render error state
	if (error) {
		return (
			<AppLayout
				title="Withdrawal Transactions"
				subtitle="View all withdrawal transactions"
			>
				<Alert
					message="Error Loading Transactions"
					description="We couldn't load your withdrawal transactions. Please try again later."
					type="error"
					showIcon
					className="mb-4"
				/>
			</AppLayout>
		);
	}

	return (
		<AppLayout
			title="Withdrawal Transactions"
			subtitle="View all withdrawal transactions"
		>
			{/* Balance Information Header Card */}
			<Card className="mb-6 overflow-hidden">
				<div>
					<Title
						level={4}
						className="mb-2"
					>
						Balance Information
					</Title>
					<Text
						type="secondary"
						className="mb-4 block"
					>
						Your current balance and withdrawal status
					</Text>

					<Row
						gutter={[16, 16]}
						className="mt-4"
					>
						<Col
							xs={24}
							sm={12}
						>
							<div className="flex items-center gap-3">
								<div
									className="flex h-10 w-10 items-center justify-center rounded-full"
									style={{ backgroundColor: '#722ed115' }}
								>
									<FaRightLeft className="text-[#722ed1]" />
								</div>
								<div>
									<div className="text-sm text-gray-500">Amount Withdrawn</div>
									<div className="text-xl font-bold">
										{isSummaryLoading ? (
											<Skeleton.Button
												active
												size="small"
												style={{ width: 80 }}
											/>
										) : (
											formatCurrency(summaryData?.results?.amount_withdrawn || 0)
										)}
									</div>
								</div>
							</div>
						</Col>
						<Col
							xs={24}
							sm={12}
						>
							<div className="flex items-center gap-3">
								<div
									className="flex h-10 w-10 items-center justify-center rounded-full"
									style={{ backgroundColor: '#eb2f9615' }}
								>
									<FaWallet className="text-[#eb2f96]" />
								</div>
								<div>
									<div className="text-sm text-gray-500">Available for Withdrawal</div>
									<div className="text-xl font-bold">
										{isSummaryLoading ? (
											<Skeleton.Button
												active
												size="small"
												style={{ width: 80 }}
											/>
										) : (
											formatCurrency(summaryData?.results?.balance_available_for_withdrawal || 0)
										)}
									</div>
								</div>
							</div>
						</Col>
					</Row>
				</div>
			</Card>

			{/* Transactions Table */}
			<Card>
				{data && data.results.length > 0 ? (
					<Table
						dataSource={data.results}
						columns={columns}
						rowKey="id"
						pagination={{
							total: data.count,
							pageSize: 20,
							showSizeChanger: false,
							showTotal: (total) => `Total ${total} transactions`,
						}}
						className="transaction-table"
						scroll={{ x: 'max-content' }}
					/>
				) : (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={
							<div className="py-8 text-center">
								<p className="mb-2 text-gray-500">You haven't made any withdrawal transactions yet.</p>
								<p className="text-gray-400">Withdrawal transactions will appear here when you request withdrawals.</p>
							</div>
						}
					/>
				)}
			</Card>

			{/* Add custom CSS inline */}
			<style>
				{`
				.transaction-table .ant-table-thead > tr > th {
					background-color: #f8fafc;
				}
				.transaction-table .ant-table-tbody > tr:hover > td {
					background-color: #f1f5f9;
				}
				`}
			</style>
		</AppLayout>
	);
};

export default Withdrawals;
